// Port of the legacy `state_disabled?` helper from
// app/helpers/application_helper.rb. Decides which CustomerOrder state
// options should be greyed-out in the Status dropdown given:
//   - the order's current state
//   - the order's `created_at` and `ghost_id` (legacy lock-out for stale
//     new_deal records — orders that pre-date 2025-10-04 and have no
//     ghost_id can't be moved at all, matching the haml behaviour)
//   - the signed-in user's email (Bomi's lock — can't cross the
//     vendor_payments boundary in either direction)
//
// Algorithm in plain English:
//   1. Bomi (bomi@fyragroup.com) can only move within the half of the
//      timeline that contains the current state — crossing the
//      vendor_payments boundary in either direction is blocked.
//   2. Stale `new_deal` (no ghost_id + created_at < 2025-10-04) — every
//      target is disabled (the legacy guard locks the dropdown).
//   3. `parent_with_closed_gmv` is never selectable from the dropdown.
//   4. States outside the linear timeline (stock, samples, consignment,
//      clubs, sub_id, closed_lost, vendor_po_issues) — always enabled,
//      they're escape hatches.
//   5. For timeline states: transitions are allowed only between
//      adjacent stages. Skipping >1 stage is blocked. Coming from a
//      non-timeline state (eg stock) you can only move to new_deal.

// Linear stage groups (states within a single stage are equivalent).
// Order matters — adjacent stages can transition, non-adjacent can't.
const TIMELINE_STAGES: Record<string, string[]> = {
  order_placement:     ['new_deal', 'deals_to_review', 'deals_to_place', 'deals_requiring_deposit'],
  order_confirmation:  ['unconfirmed_vendor_po', 'confirmed_orders'],
  vendor_payment:      ['vendor_payments'],
  transit_time_to_wh:  ['needs_routing', 'shipped_from_brand'],
  shipment_rework:     ['in_warehouse', 'scanned', 'partially_scanned'],
  customer_payment:    ['ready_to_ship'],
  routing:             ['released'],
  end:                 ['shipped_to_client'],
}
const TIMELINE_STAGE_NAMES = Object.keys(TIMELINE_STAGES)
const TIMELINE_STATES = new Set(Object.values(TIMELINE_STAGES).flat())

// Same horizontal order as legacy sorted_order_states_options — used
// only for Bomi's vendor_payments-boundary check.
const SORTED_STATES = [
  'new_deal', 'deals_to_review', 'deals_to_place', 'deals_requiring_deposit',
  'unconfirmed_vendor_po', 'confirmed_orders',
  'vendor_payments',
  'needs_routing', 'shipped_from_brand',
  'in_warehouse', 'scanned', 'partially_scanned',
  'ready_to_ship',
  'released',
  'shipped_to_client',
  'vendor_po_issues',
  'samples', 'consignment', 'stock',
  'clubs', 'parent_with_closed_gmv',
]

const NEW_DEAL_LOCK_DATE = new Date('2025-10-04T00:00:00Z').getTime()

function stageOf(state: string): string | null {
  for (const stage of TIMELINE_STAGE_NAMES) {
    if (TIMELINE_STAGES[stage].includes(state)) return stage
  }
  return null
}

export interface StateGuardContext {
  currentState: string
  ghostId: number | string | null | undefined
  createdAt: string | null | undefined  // ISO8601
  currentUserEmail: string | null | undefined
}

export function isStateDisabled(targetState: string, ctx: StateGuardContext): boolean {
  const { currentState, ghostId, createdAt, currentUserEmail } = ctx

  // 1. Bomi's vendor_payments fence
  if (currentUserEmail === 'bomi@fyragroup.com') {
    const vpIdx     = SORTED_STATES.indexOf('vendor_payments')
    const currIdx   = SORTED_STATES.indexOf(currentState)
    const targetIdx = SORTED_STATES.indexOf(targetState)
    if (vpIdx < 0 || currIdx < 0 || targetIdx < 0) return true
    if (currIdx < vpIdx && targetIdx >= vpIdx) return true
    if (currIdx > vpIdx && targetIdx < currIdx) return true
  }

  // 2. Stale new_deal lock — no ghost, predates the cutoff → freeze
  if (currentState === 'new_deal' && !ghostId && createdAt) {
    if (new Date(createdAt).getTime() < NEW_DEAL_LOCK_DATE) return true
  }

  // 3. parent_with_closed_gmv is never selectable
  if (targetState === 'parent_with_closed_gmv') return true

  // 4. Off-timeline targets are always allowed
  if (!TIMELINE_STATES.has(targetState)) return false

  // 5. Coming from off-timeline (eg stock) — only new_deal is allowed
  if (!TIMELINE_STATES.has(currentState)) {
    return targetState !== 'new_deal'
  }

  // 6. Within timeline — must be the same or adjacent stage
  const currentStage = stageOf(currentState)
  const targetStage  = stageOf(targetState)
  if (!currentStage || !targetStage) return true

  const currIdx = TIMELINE_STAGE_NAMES.indexOf(currentStage)
  const trgtIdx = TIMELINE_STAGE_NAMES.indexOf(targetStage)
  return Math.abs(trgtIdx - currIdx) > 1
}
